import { useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { LoadingContext } from "./LoadingContext";
import { UserInfoContext } from "./UserInfoContext";
import { SidebarContext, type SidebarContextType } from './SidebarContext';
import { WsContext } from "./WsContext";
import { ToastProvider } from "./ToastProvider";
import { useGetMe, useUserMeWebSocket } from "@/hooks/userHook";

interface Props {
  children: ReactNode;
}

const AppContext = ({ children }: Props) => {
  const [loadingCount, setLoadingCount] = useState<number>(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const sidebarContextValue: SidebarContextType = {
    isSidebarOpen,
    toggleSidebar: () => setIsSidebarOpen(!isSidebarOpen),
    setIsSidebarOpen,
  }; 
  const { data: user, isLoading: isUserLoading } = useGetMe();
  const { isConnected, wsData } = useUserMeWebSocket();

  const increaseLoading = useCallback(() => setLoadingCount((c) => c + 1), []);
  const decreaseLoading = useCallback(() => setLoadingCount((c) => Math.max(0, c - 1)), []);
  const isLoading = loadingCount > 0;

  useEffect(() => {
    if (!isUserLoading) return;
    increaseLoading();
    return () => decreaseLoading();
  }, [isUserLoading, increaseLoading, decreaseLoading]);

  return (
    <ToastProvider>
      <WsContext.Provider value={{ isConnected, wsData }}>
        <SidebarContext.Provider value={sidebarContextValue}>
          <LoadingContext.Provider value={{ isLoading, increaseLoading, decreaseLoading }}>
            <UserInfoContext.Provider value={user ?? null}>
              {children}
            </UserInfoContext.Provider>
          </LoadingContext.Provider>
        </SidebarContext.Provider>
      </WsContext.Provider>
    </ToastProvider>
  );
};

export default AppContext;
